'use client'

import { useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { EllipsisVertical } from 'lucide-react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'

import { withAuthentication } from '~/components/auth/withAuthentication'
import {
  CheckboxGroupInput,
  Copyable,
  List,
  PageLayout,
  Pill,
  TextInput,
} from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { Dropdown } from '~/components/component-library/Dropdown'
import { Timestamp } from '~/components/component-library/Timestamp'
import {
  SettingsPageContainer,
  settingsVisibility,
} from '~/components/settings/utils'
import { API_KEY_SCOPE_OPTIONS } from '~/constants/apiKeys'
import { QUERY_KEYS } from '~/constants/react-query'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg, useActiveOrgMember } from '~/hooks/org'
import { useQueryWrapper } from '~/hooks/useQueryWrapper'
import { useServerRequests } from '~/hooks/useRequests'

type ApiKey = {
  id: string
  name: string
  createdAt: string
  apiKey?: string
  scopes?: string[]
  keyPrefix?: string | null
  lastUsedAt?: string | null
}

const TeamApiKeysPageInner = () => {
  return (
    <SettingsPageContainer
      requireOwner={settingsVisibility['api-keys'] === 'owners'}
    >
      <PageLayout
        pageType="standard"
        title="API keys"
        description="Create and manage API keys for the CLI and MCP integrations."
      >
        <Copyable
          layout="field"
          label="Add your agent"
          value="Set up the Splits CLI: https://splits.org/docs/introduction/agents"
        />
        <ApiKeysList />
      </PageLayout>
    </SettingsPageContainer>
  )
}

const ApiKeysList = () => {
  const { org: activeOrg } = useActiveOrg()
  const orgMember = useActiveOrgMember()
  const isOwner = orgMember?.role === 'OWNER'
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const { serverGetRequest } = useServerRequests()
  const queryClient = useQueryClient()

  const { data } = useQueryWrapper<{ keys: ApiKey[] }>({
    queryKey: [QUERY_KEYS.ORG_API_KEYS, activeOrg?.id],
    enabled: !!activeOrg?.id,
    queryFn: async () => {
      const result = await serverGetRequest<{ keys: ApiKey[] }>({
        path: `/api/org/keys?orgId=${activeOrg!.id}`,
        errorMessage: 'Failed to load API keys',
      })
      if (!result?.data) throw new Error('Failed to load API keys')
      return {
        keys: result.data.keys,
      }
    },
  })

  const keys = data?.keys ?? []

  const invalidateKeys = () => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.ORG_API_KEYS, activeOrg?.id],
    })
  }

  const items = keys.map((key) => ({
    key: key.id,
    content: key.name,
    description: (
      <>
        <Timestamp timestamp={key.createdAt} type="absolute" />
        {key.keyPrefix && (
          <>
            {' · '}
            <span className="font-mono">{key.keyPrefix}...</span>
          </>
        )}
      </>
    ),
    meta: (
      <div className="flex items-center gap-2">
        {(key.scopes ?? ['read']).map((scope) => (
          <Pill
            key={scope}
            size="base"
            label={scope.charAt(0).toUpperCase() + scope.slice(1)}
          />
        ))}
      </div>
    ),
    actionIcon: (
      <Dropdown
        items={[
          {
            content: 'Delete key...',
            onClick: () =>
              setGlobalModalState({
                isOpen: true,
                content: (
                  <DeleteApiKeyDialog
                    apiKey={key}
                    closeDialog={closeGlobalModal}
                    onDelete={invalidateKeys}
                  />
                ),
              }),
          },
        ]}
      >
        <button type="button">
          <EllipsisVertical size={16} />
        </button>
      </Dropdown>
    ),
  }))

  return (
    <List
      label="API keys"
      items={items}
      cta={{
        label: 'Create API key',
        onClick: () => {
          setGlobalModalState({
            isOpen: true,
            content: (
              <CreateApiKeyDialog
                closeDialog={closeGlobalModal}
                onCreated={invalidateKeys}
                isOwner={isOwner}
              />
            ),
          })
        },
      }}
    />
  )
}

const CreateApiKeySchema = z.object({
  name: z.string().trim().min(1, 'Required'),
})

const CreateApiKeyDialog = ({
  closeDialog,
  onCreated,
  isOwner,
}: {
  closeDialog: () => void
  onCreated: () => void
  isOwner: boolean
}) => {
  const { org: activeOrg } = useActiveOrg()
  const { serverPostRequest, isProcessing } = useServerRequests()
  const [scopes, setScopes] = useState<string[]>(['read'])
  const [createdKey, setCreatedKey] = useState<string | null>(null)

  const scopeOptions = API_KEY_SCOPE_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
    description: option.description,
    disabled: option.value === 'owner' && !isOwner,
  }))

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(CreateApiKeySchema),
    mode: 'onChange',
    defaultValues: { name: '' },
  })

  const createKey: SubmitHandler<z.infer<typeof CreateApiKeySchema>> = async ({
    name,
  }) => {
    const result = await serverPostRequest<{ key: ApiKey }>({
      path: '/api/org/keys',
      params: { orgId: activeOrg!.id, name, scopes },
      successMessage: 'API key created',
      errorMessage: 'Failed to create API key',
    })
    if (result?.data?.key?.apiKey) {
      setCreatedKey(result.data.key.apiKey)
      onCreated()
    }
  }

  if (createdKey) {
    return (
      <SplitsDialogContent
        id="apiKeyCreated"
        variant="cta"
        title="API key created"
        actionButton={{
          text: 'Done',
          onClick: closeDialog,
        }}
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-secondary">
            Copy your API key now. You won&apos;t be able to see it again.
          </p>
          <Copyable layout="field" value={createdKey} />
        </div>
      </SplitsDialogContent>
    )
  }

  return (
    <SplitsDialogContent
      id="createApiKey"
      variant="cta"
      title="Create API key"
      isLoading={isProcessing}
      actionButton={{
        text: 'Create',
        onClick: handleSubmit(createKey),
        isDisabled: !isValid || scopes.length === 0,
      }}
      cancelButton={{ text: 'Cancel' }}
    >
      <div className="flex flex-col gap-4">
        <TextInput
          control={control}
          inputName="name"
          label="Key name"
          placeholder="CLI access"
          autoFocus
        />
        <CheckboxGroupInput
          label="Permissions"
          options={scopeOptions}
          value={scopes}
          onChange={setScopes}
        />
      </div>
    </SplitsDialogContent>
  )
}

const DeleteApiKeyDialog = ({
  apiKey,
  closeDialog,
  onDelete,
}: {
  apiKey: ApiKey
  closeDialog: () => void
  onDelete: () => void
}) => {
  const { serverDeleteRequest, isProcessing } = useServerRequests()

  const deleteKey = async () => {
    const result = await serverDeleteRequest({
      path: `/api/org/keys/${apiKey.id}`,
      successMessage: 'API key deleted',
      errorMessage: 'Failed to delete API key',
    })
    if (result) {
      onDelete()
      closeDialog()
    }
  }

  return (
    <SplitsDialogContent
      id="deleteApiKey"
      variant="destructive"
      title={`Delete "${apiKey.name}" API key?`}
      isLoading={isProcessing}
      deleteButton={{
        text: 'Delete',
        onClick: deleteKey,
      }}
    >
      <p>
        This key will be permanently deleted and any integrations using it will
        stop working immediately.
      </p>
    </SplitsDialogContent>
  )
}
export const TeamApiKeysPage = withAuthentication(TeamApiKeysPageInner)
